import { Calendar, ArrowUpRight, Tag } from "lucide-react";
import { useInView } from "../hooks/useInView";

const posts = [
  {
    tag: "Product",
    date: "Dec 12, 2025",
    title: "Introducing Relationship Warmth Scores",
    excerpt: "We built a proprietary algorithm that scores every contact in your network from 0-100 based on email frequency, response times, and sentiment. Now you'll never let a warm lead go cold.",
    color: "bg-[#ff9971] text-[#772f0f]",
  },
  {
    tag: "AI",
    date: "Dec 5, 2025",
    title: "Claude Sonnet 4.5 Powers Smarter Drafts",
    excerpt: "We upgraded our AI engine to Anthropic's latest Claude Sonnet 4.5 model. Email drafts are now more context-aware, with better tone matching and relationship-specific language.",
    color: "bg-[#a33900]/10 text-[#a33900]",
  },
  {
    tag: "Launch",
    date: "Nov 28, 2025",
    title: "Relio Scale: Built for Founding Teams",
    excerpt: "Multi-user collaboration, unlimited AI drafts, LinkedIn ingestion, and custom pipeline stages. Everything a high-velocity founding team needs to manage their network at scale.",
    color: "bg-[#f0edf0] text-[#5a4138]",
  },
  {
    tag: "Integration",
    date: "Nov 15, 2025",
    title: "Gmail Deep Sync is Live",
    excerpt: "Full bi-directional Gmail sync is here. Send, receive, and track emails without leaving Relio. Every thread auto-links to the right contact in your pipeline.",
    color: "bg-[#ff9971] text-[#772f0f]",
  },
];

const changelog = [
  { version: "v2.4.0", date: "Dec 10", items: ["Warmth decay alerts", "Bulk email draft", "Pipeline export to CSV"] },
  { version: "v2.3.0", date: "Nov 22", items: ["LinkedIn data ingestion", "Custom pipeline stages", "Team workspaces"] },
  { version: "v2.2.0", date: "Nov 8", items: ["Claude Sonnet 4.5 upgrade", "Email open tracking", "Mobile responsive sidebar"] },
  { version: "v2.1.0", date: "Oct 25", items: ["Intro path visualization", "Contact deduplication", "Dark mode sidebar"] },
];

export default function BlogSection() {
  const [sectionRef, isInView] = useInView();

  return (
    <section
      id="blog"
      data-testid="blog-section"
      ref={sectionRef}
      className="pl-8 md:pl-16 pr-4 md:pr-8 py-16 md:py-24"
    >
      <div
        className={`transition-all duration-700 ${
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Header */}
        <div className="mb-16">
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
            Blog & Changelog
          </span>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d]">
            What's new at Relio.
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#5a4138] max-w-lg">
            Product updates, AI improvements, and the thinking behind our decisions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Blog posts */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {posts.map((post, i) => (
                <article
                  key={post.title}
                  data-testid={`blog-post-${i}`}
                  className={`
                    bg-[#f6f2f5] rounded-3xl p-7 group hover:bg-[#f0edf0] transition-all duration-300 cursor-pointer
                    ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
                  `}
                  style={{ transitionDelay: `${i * 100 + 200}ms`, transitionDuration: "600ms" }}
                >
                  <div className="flex items-center gap-2.5 mb-4">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full ${post.color}`}>
                      <Tag size={8} className="inline mr-1" />
                      {post.tag}
                    </span>
                    <span className="text-[10px] text-[#5a4138] flex items-center gap-1">
                      <Calendar size={10} />
                      {post.date}
                    </span>
                  </div>
                  <h3 className="text-base font-medium text-[#1c1b1d] mb-2 group-hover:text-[#a33900] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-[#5a4138] leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-xs font-medium text-[#a33900] opacity-0 group-hover:opacity-100 transition-opacity">
                    Read more <ArrowUpRight size={12} />
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Changelog */}
          <div className="lg:col-span-4">
            <div className="bg-[#f0edf0] rounded-3xl p-7">
              <h3 className="text-sm font-medium text-[#1c1b1d] mb-5 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#a33900]" />
                Changelog
              </h3>
              <div className="space-y-5">
                {changelog.map((release, i) => (
                  <div
                    key={release.version}
                    data-testid={`changelog-${i}`}
                    className={`
                      ${isInView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}
                    `}
                    style={{ transitionDelay: `${i * 100 + 400}ms`, transitionDuration: "500ms" }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-[#a33900]">{release.version}</span>
                      <span className="text-[10px] text-[#5a4138]">{release.date}</span>
                    </div>
                    <ul className="space-y-1">
                      {release.items.map((item) => (
                        <li key={item} className="text-xs text-[#5a4138] flex items-start gap-2">
                          <span className="w-1 h-1 rounded-full bg-[#e2bfb2] mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
